import { useEffect, useState, type RefObject } from 'react';
import { onDeviceChange } from './device-db';
import { refreshJob } from './jobs';

/** Whether the browser thinks it has a connection, kept current. */
export function useOnline(): boolean {
  const [online, setOnline] = useState(() => navigator.onLine);
  useEffect(() => {
    const update = () => setOnline(navigator.onLine);
    window.addEventListener('online', update);
    window.addEventListener('offline', update);
    return () => {
      window.removeEventListener('online', update);
      window.removeEventListener('offline', update);
    };
  }, []);
  return online;
}

/** Read from device storage, and read again whenever anything on the device changes. */
export function useDeviceQuery<T>(read: () => Promise<T>, deps: unknown[]): T | undefined {
  const [value, setValue] = useState<T>();
  useEffect(() => {
    let current = true;
    const load = () => {
      read().then((result) => {
        if (current) setValue(result);
      }, console.error);
    };
    load();
    const stop = onDeviceChange(load);
    return () => {
      current = false;
      stop();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- callers pass the read's own inputs as deps
  }, deps);
  return value;
}

/**
 * With signal, bring the device's copy of the job up to date with CRM when a page of it opens or reloads: the
 * job page, the desk, the Schedule of Work and the site plan alike. Changes made elsewhere (another device, an
 * Accept the desk didn't hear back from) otherwise stay unseen until the job page is opened (#515, 23 Oct 2026).
 */
export function useJobRefresh(jobNumber: string | null | undefined): void {
  useEffect(() => {
    if (jobNumber) void refreshJob(jobNumber);
  }, [jobNumber]);
}

/**
 * Keep a touch that starts on this element from scrolling the page. iPad
 * Safari doesn't always honour `touch-action: none` on an SVG drawing layer:
 * it scrolls the page instead, cancels the pointer, and the stroke is lost
 * (#670). A touchmove listener that isn't passive (React's are) stops it.
 */
export function useNoTouchScroll(element: RefObject<HTMLElement | null>): void {
  useEffect(() => {
    const target = element.current;
    if (!target) return;
    const stop = (e: TouchEvent) => e.preventDefault();
    target.addEventListener('touchmove', stop, { passive: false });
    return () => target.removeEventListener('touchmove', stop);
  }, [element]);
}

/** The CSS variable holding the sticky app header's height, for what sticks under it (#652): sync-status.tsx names it in its class. */
const HEADER_HEIGHT_VAR = '--app-header-height';

/**
 * Keep `HEADER_HEIGHT_VAR` on `shell` set to the height of the `<header>`
 * inside it, so a bar sticky at `top: var(--app-header-height)` parks under
 * the header rather than over it. The header's height changes with width.
 */
export function useHeaderHeight(shell: RefObject<HTMLElement | null>): void {
  useEffect(() => {
    const target = shell.current;
    const header = target?.querySelector('header');
    if (!target || !header) return;
    const update = () => target.style.setProperty(HEADER_HEIGHT_VAR, `${header.getBoundingClientRect().height}px`);
    update();
    const watch = new ResizeObserver(update);
    watch.observe(header);
    return () => watch.disconnect();
  }, [shell]);
}
